import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { providersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, ErrorNote, Loading } from '@/components/common/Ui'
import { relative } from '@/lib/format'
import { PERMS, can, useAuth } from '@/store/auth'

/**
 * The AI services this shop has connected.
 *
 * A provider knows its own address, so connecting one is a name and a key —
 * not a key field beside an endpoint URL that an operator has to know
 * belongs to OpenRouter rather than OpenAI.
 *
 * A key is never sent back here. Its last four characters are, so somebody
 * can tell which key is in force.
 */
// Services the shop does not use. Hidden here rather than removed from the
// catalogue, because the catalogue is also what resolves a model name to a
// service — dropping an entry would change how the others fall back. A card
// offering to connect something nobody will connect is one more thing to
// read past on a page of settings.
const HIDDEN = new Set(['openai'])

export default function AiProviders() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const canEdit = can(user, PERMS.settingsWrite)

  const [drafts, setDrafts] = useState({})
  const [error, setError] = useState('')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: providersApi.list,
  })

  const onDone = (fresh) => {
    queryClient.setQueryData(['ai-providers'], fresh)
    setError('')
  }

  const connect = useMutation({
    mutationFn: ({ code, key }) => providersApi.connect(code, key),
    onSuccess: (fresh, { code }) => {
      onDone(fresh)
      setDrafts((d) => ({ ...d, [code]: undefined }))
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const disconnect = useMutation({
    mutationFn: (code) => providersApi.disconnect(code),
    onSuccess: onDone,
    onError: (e) => setError(errorMessage(e)),
  })

  // Checked one at a time, so a slow service does not hold up the others.
  const check = useMutation({
    mutationFn: (code) => providersApi.check(code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-providers'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load the services" onRetry={refetch} />

  const capabilities = (p) =>
    [
      p.reads_speech && 'voice notes',
      p.reads_images && 'photographs',
      p.reads_text && 'order text',
      // Listed separately from 'order text': a service can be trusted with a
      // product name an admin reads before saving and still not be what
      // reads an order into priced lines.
      p.translates_text && 'Tamil names',
    ].filter(Boolean).join(' · ')

  return (
    <>
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      <p className="hint">
        Connect a service once here, then choose it on the Reading orders tab.
      </p>

      <div className="provider-list">
        {data.filter((p) => !HIDDEN.has(p.code)).map((p) => {
          const editing = drafts[p.code] !== undefined
          return (
            <section className="panel provider" key={p.code}>
              <div className="provider-head">
                <div>
                  <strong>{p.label}</strong>
                  {p.is_connected ? (
                    <span className="tag tag-ok">connected</span>
                  ) : (
                    <span className="tag">not connected</span>
                  )}
                  {p.from_env && (
                    <span className="tag" title="Set in the server's .env file">
                      from .env
                    </span>
                  )}
                </div>
                {p.is_connected && canEdit && (
                  <button
                    className="btn btn-sm"
                    disabled={check.isPending}
                    onClick={() => check.mutate(p.code)}
                  >
                    {check.isPending && check.variables === p.code
                      ? 'Testing…'
                      : `Test ${p.label}`}
                  </button>
                )}
              </div>

              <p className="hint">{p.note}</p>
              <p className="hint">Reads: {capabilities(p) || '—'}</p>

              {/* When it was last asked, not whether a key is stored — the
                  tag beside the name already says that, and both reading
                  'connected' left the card saying it twice. */}
              {p.last_check_detail && (
                <p className={`hint ${p.last_check_ok ? 'success' : 'caution'}`}>
                  {p.last_check_ok ? '✓' : '✕'} {p.last_check_detail}
                  {p.last_checked_at && ` · ${relative(p.last_checked_at)}`}
                </p>
              )}

              <div className="provider-key">
                {p.is_connected && !editing ? (
                  <>
                    <span className="secret-mask">
                      ●●●● {p.hint ? p.hint.replace('…', '') : ''}
                    </span>
                    {canEdit && (
                      <>
                        <button
                          className="btn btn-sm"
                          onClick={() => setDrafts((d) => ({ ...d, [p.code]: '' }))}
                        >
                          Replace
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => disconnect.mutate(p.code)}
                        >
                          Disconnect {p.label}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type="password"
                      autoComplete="new-password"
                      disabled={!canEdit}
                      placeholder={`Key from ${p.key_from}`}
                      value={drafts[p.code] ?? ''}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [p.code]: e.target.value }))
                      }
                    />
                    {/* Named, not just 'Connect': three cards with the same
                        button is how a key ends up on the wrong service, and
                        connecting replaces whatever was there. */}
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!canEdit || !drafts[p.code] || connect.isPending}
                      onClick={() =>
                        connect.mutate({ code: p.code, key: drafts[p.code] })
                      }
                    >
                      {p.is_connected ? `Replace ${p.label} key` : `Connect ${p.label}`}
                    </button>
                    {p.is_connected && (
                      <button
                        className="btn btn-sm"
                        onClick={() =>
                          setDrafts((d) => ({ ...d, [p.code]: undefined }))
                        }
                      >
                        Keep the current one
                      </button>
                    )}
                  </>
                )}
              </div>
            </section>
          )
        })}
      </div>
    </>
  )
}
